import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { Input, Select } from '@insly/qmt-reactjs-ui-lib';
import { JSONSchema, TZipCodeResult } from 'models';
import { ContentBodyRow } from 'app/Pages/Quote/Profile/body/styles';
import { useApp } from 'context/App';
import {
  fetchCityData,
  fetchZipCodeData,
  filterStreets,
  filterStreetsWithoutCity,
  setStreets,
  translate,
  translateTitleInScheme,
  zipCodeMatcher,
} from 'utils';

const SEARCH_DELAY = 400;
const MIN_VALUE_LENGTH_TO_START_SEARCH = 3;

const Row = styled(ContentBodyRow)`
  & > * {
    min-width: 0;
  }
`;

interface IZipCodeAddressInputs {
  address: Record<string, any> | undefined,
  schemaProps: JSONSchema,
  disabled?: boolean,
  required?: boolean,
  onChange: (address: Record<string, any>) => void,
  errors?: Record<string, string>,
}

export const ZipCodeAddressInputs = ({
  address,
  schemaProps,
  disabled,
  required,
  onChange,
  errors = {},
}: IZipCodeAddressInputs) => {
  const { showNotification } = useApp();

  const cancelTokenSourceRef = useRef(axios.CancelToken.source());
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const [streets, updateStreets] = useState<{ key: string, value: string }[]>([]);
  const [cities, updateCities] = useState<{ key: string, value: string }[]>([]);
  const [isFetching, updateIsFetching] = useState(false);

  useEffect(() => {
    if (address?.postalCode?.length === 6) {
      updateIsFetching(true);
      fetchZipCodeData(
        address.postalCode,
        (results) => {
          const filteredStreets = filterStreets(results, address.city);
          if (filteredStreets.length > 1) {
            setStreets(filteredStreets, updateStreets);
          }
          updateIsFetching(false);
        },
        updateCities,
        () => {},
        () => {},
        onError,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onError = () => {
    updateIsFetching(false);
    showNotification({
      preset: 'error',
      message: translate({ key: 'common.error' }),
      autoHide: true,
    });
  };

  const updateField = (field: string, value: any) => {
    onChange({ ...(address || {}), [field]: value });
  };

  const updateFields = (fields: Record<string, string>) => {
    onChange({ ...(address || {}), ...fields });
  };

  const setAddressFromZip = (zipCodeResults: TZipCodeResult[]) => {
    const result = zipCodeResults[0];
    updateFields({
      city: result.city || '',
      street: result.street || '',
    });
  };

  const handlePostalCodeChange = (_: any, value: any) => {
    const formatted = zipCodeMatcher(value, address?.postalCode || '');
    updateField('postalCode', formatted);
  };

  const handlePostalCodeBlur = () => {
    const postalCode = address?.postalCode || '';
    if (postalCode.length === 6) {
      updateIsFetching(true);
      fetchZipCodeData(
        postalCode,
        (results: TZipCodeResult[]) => {
          if (results.length > 1) {
            const filteredStreets = filterStreets(results, address?.city);
            if (filteredStreets.length > 1) {
              setStreets(filteredStreets, updateStreets);
            }
          }
          updateIsFetching(false);
        },
        updateCities,
        setAddressFromZip,
        updateStreets,
        onError,
      );
    }
  };

  const handleCityChange = (_: any, value: any) => {
    if (cities.length && value?.length >= MIN_VALUE_LENGTH_TO_START_SEARCH) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        updateIsFetching(true);
        cancelTokenSourceRef.current.cancel('Operation canceled by the user.');
        cancelTokenSourceRef.current = axios.CancelToken.source();
        fetchCityData(address?.postalCode || '', value, cancelTokenSourceRef.current, (results) => {
          const addressUpdate: Record<string, string> = { city: value };
          if (results.length) {
            addressUpdate.city = results[0].city;
          }
          const filteredStreets = filterStreetsWithoutCity(results);
          if (filteredStreets.length > 1) {
            addressUpdate.street = '';
            setStreets(filteredStreets, updateStreets);
          } else {
            addressUpdate.street = filteredStreets?.length ? filteredStreets[0].street : value;
            updateStreets([]);
          }
          updateFields(addressUpdate);
          updateIsFetching(false);
        }, onError);
      }, SEARCH_DELAY);
    } else {
      updateField('city', value);
      updateStreets([]);
    }
  };

  const isDisabled = isFetching || disabled;

  return (
    <>
      <Row gridTemplate="1fr 2fr">
        <Input
          name="postalCode"
          label={translateTitleInScheme(schemaProps.postalCode)}
          placeholder={'""-"""'}
          value={address?.postalCode || ''}
          handleChange={handlePostalCodeChange}
          onBlur={handlePostalCodeBlur}
          disabled={isDisabled}
          required={required}
          error={errors.postalCode}
        />
        {cities.length ? (
          <Select
            name="city"
            label={translateTitleInScheme(schemaProps.city)}
            value={address?.city || ''}
            options={cities}
            handleChange={handleCityChange}
            searchable
            applyCustomValueOnSearch
            customValue={address?.city || ''}
            disabled={isFetching || disabled}
            required={required}
            error={errors.city}
            placeholder={translate({ key: 'common.select' })}
            notFoundText={translate({ key: 'common.nothing_found' })}
          />
        ) : (
          <Input
            name="city"
            label={translateTitleInScheme(schemaProps.city)}
            value={address?.city || ''}
            handleChange={(_: any, value: any) => updateField('city', value)}
            disabled={isDisabled}
            required={required}
            error={errors.city}
          />
        )}
      </Row>

      <Row gridTemplate="3fr 1fr 1fr">
        {streets.length ? (
          <Select
            name="street"
            label={translateTitleInScheme(schemaProps.street)}
            value={address?.street || ''}
            options={streets}
            handleChange={(_: any, value: any) => updateField('street', value)}
            searchable
            applyCustomValueOnSearch
            customValue={address?.street || ''}
            disabled={isFetching || disabled}
            required={required}
            error={errors.street}
            placeholder={translate({ key: 'common.select' })}
            notFoundText={translate({ key: 'common.nothing_found' })}
          />
        ) : (
          <Input
            name="street"
            label={translateTitleInScheme(schemaProps.street)}
            value={address?.street || ''}
            handleChange={(_: any, value: any) => updateField('street', value)}
            disabled={isDisabled}
            required={required}
            error={errors.street}
          />
        )}
        <Input
          name="houseNumber"
          label={translateTitleInScheme(schemaProps.houseNumber)}
          value={address?.houseNumber || ''}
          handleChange={(_: any, value: any) => updateField('houseNumber', value)}
          disabled={isDisabled}
          required={required}
          error={errors.houseNumber}
        />
        <Input
          name="flatNumber"
          label={translateTitleInScheme(schemaProps.flatNumber)}
          value={address?.flatNumber || ''}
          handleChange={(_: any, value: any) => updateField('flatNumber', value)}
          disabled={isDisabled}
          error={errors.flatNumber}
        />
      </Row>
    </>
  );
};

export default ZipCodeAddressInputs;
